import { useQuery } from '@tanstack/react-query';
import React, { useEffect } from 'react';
import { Button, Message } from 'ui';
import { displayPrice } from 'utils';
import {
  DiscountedShippingOptions,
  fetchDiscountedShippingOptions,
} from '~/api/listFlow';
import HelpLink from '~/components/Links/HelpLink';
import HelpIcon from '~/public/static/svg/HelpIcon';
import { getListFlowPricing } from '~/store/listFlow/slices/pricing';
import {
  changeShippingDiscount,
  getListFlowDefaultParcel,
  getListFlowSelectedDiscountedOption,
  getListFlowShipsFromCountry,
} from '~/store/listFlow/slices/shipping';
import { useAppDispatch, useAppSelector } from '~/store/store';
import {
  DiscountedShippingCost,
  DiscountedShippingDiscount,
} from '~/typings/services/commerce/shipping';
import { RailsCategory } from '~/typings/services/rails/item';
import { DISCOUNT_ENCOURAGEMENT_THRESHOLD } from '../../../constants/discountEncouragementThreshold';
import { minutes5inMS } from '../../../constants/times';

function displayCost(cost: DiscountedShippingCost): string {
  if ('high_amount' in cost) {
    if (cost.low_amount != null && cost.low_amount !== cost.high_amount) {
      return `${displayPrice(cost.low_amount)} - ${displayPrice(
        cost.high_amount,
      )}`;
    } else {
      return displayPrice(cost.high_amount);
    }
  }

  return displayPrice(cost.amount);
}

function getHighestAmount(cost?: DiscountedShippingCost | null): number {
  if (!cost) {
    return 0;
  }

  if ('high_amount' in cost) {
    return Math.max(cost.low_amount, cost.high_amount);
  }

  return cost.amount;
}

interface Props {
  category: RailsCategory;
}

const ListFlowDiscountedShipping: React.FC<Props> = ({ category }) => {
  const dispatch = useAppDispatch();
  const pricing = useAppSelector(getListFlowPricing);
  const parcel = useAppSelector(getListFlowDefaultParcel);
  const shipsFromCountry = useAppSelector(getListFlowShipsFromCountry);
  const selectedDiscount = useAppSelector(getListFlowSelectedDiscountedOption);

  const options: DiscountedShippingOptions = {
    shipsFromCountry,
    defaultParcel: true,
    parcelUuid: parcel?.uuid,
    categoryUuid: category.uuid,
    listPrice: pricing.listPrice,
  };

  const { data: discountedShipping } = useQuery(
    ['discounted-shipping', options],
    () => fetchDiscountedShippingOptions(options),
    {
      staleTime: minutes5inMS,
      retry: false,
      keepPreviousData: true,
    },
  );

  const selected: DiscountedShippingDiscount | undefined | null =
    discountedShipping?.discounts?.find(
      d => d.amount === selectedDiscount?.amount,
    );

  const defaultOption: DiscountedShippingDiscount | undefined | null =
    discountedShipping?.discounts?.find(d => d.default);

  useEffect(() => {
    if (discountedShipping?.discounts && !selected) {
      if (defaultOption) {
        dispatch(changeShippingDiscount({ discount: defaultOption }));
      } else {
        dispatch(changeShippingDiscount({ discount: null }));
      }
    }
  }, [defaultOption, selected, discountedShipping, dispatch]);

  if (!discountedShipping?.visible) {
    return null;
  }

  let showDiscountEncouragement = false;

  const encourageDiscountedShippingThreshold =
    discountedShipping.encourage_discount_threshold ??
    DISCOUNT_ENCOURAGEMENT_THRESHOLD;

  // Find a discount where the highest is greater than a threshold to show the
  // "discount encouraged" message.
  if (
    discountedShipping?.discounts?.find(
      d =>
        getHighestAmount(d.buyer_cost) >= encourageDiscountedShippingThreshold,
    )
  ) {
    showDiscountEncouragement = true;
  }

  return (
    <div>
      <div className="text-base font-semibold">Add a Shipping Discount</div>

      {showDiscountEncouragement && (
        <Message warning>
          <span className="flex justify-between text-sm">
            <span>Discount encouraged in this category</span>

            <HelpLink link="discountedShipping">
              <HelpIcon className="h-5 w-5" />
            </HelpLink>
          </span>
        </Message>
      )}

      <p className="text-slate-green-500 mb-2 text-sm">
        Items with free shipping are <b>65%</b> more likely to sell.
      </p>

      <div className="mb-4 flex">
        <div className="w-1/2">
          <div>Buyer Shipping Cost</div>
          <div className="text-lg font-semibold">
            {selected?.buyer_cost ? displayCost(selected.buyer_cost) : '$--'}
          </div>
        </div>

        <div className="w-1/2">
          <div>Your Shipping Cost</div>
          <div className="text-lg font-semibold">
            {selected?.seller_cost ? displayCost(selected.seller_cost) : '$--'}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 md:grid-cols-4 md:gap-4">
        {discountedShipping.discounts.map((discount, i) => (
          <Button
            key={discount.label}
            color={
              discount.amount === selected?.amount ? 'slate-green' : 'black'
            }
            disabled={!discount.available}
            onClick={() => {
              dispatch(changeShippingDiscount({ discount }));
            }}
          >
            {discount.label}
          </Button>
        ))}
      </div>

      <div className="mt-4 text-sm italic">
        Shipping discounts will be deducted from your earnings.{' '}
        {discountedShipping.hint ?? ''}
      </div>
    </div>
  );
};

export default ListFlowDiscountedShipping;
